import PropertyPanel from '../widgets/PropertyPanel.js';

/**
 * EffectsPropertyPanel - UI for special effects material properties
 * Supports: glass, metallic (H/V), holographic (H/V), neon
 */
export default class EffectsPropertyPanel extends PropertyPanel {
  render(initialData = {}) {
    this.clear();

    // Effect type selector (rotated effects removed due to mobile rendering issues)
    const typeSelect = this.addSelect('Effect', [
      { value: 'glass', label: 'Glass' },
      { value: 'metallic', label: 'Metallic' },
      { value: 'holographic', label: 'Holographic' },
      { value: 'neon', label: 'Neon' }
    ], initialData.effectType || 'glass');
    this.controls.typeSelect = typeSelect;

    // Effect options container (for direction/rotation controls)
    this.effectOptionsContainer = document.createElement('div');
    this.container.appendChild(this.effectOptionsContainer);

    // Update options when type changes
    typeSelect.addEventListener('change', () => {
      this.updateEffectOptions();
      if (this.onChange) this.onChange();
    });

    // Base color
    const baseColor = this.addColorPicker('Base Color', initialData.color || '#3498db');
    this.controls.baseColor = baseColor;
    baseColor.addEventListener('input', () => {
      if (this.onChange) this.onChange();
    });

    // Opacity slider
    const opacitySlider = this.addSlider('Opacity', 0, 1, 0.01, initialData.opacity || 1, (val) => {
      if (this.onOpacityChange) this.onOpacityChange(val);
      if (this.onChange) this.onChange();
    });
    this.controls.opacitySlider = opacitySlider;

    // Initialize options for the selected type
    this.updateEffectOptions(initialData);
  }

  /**
   * Update effect options based on selected type
   */
  updateEffectOptions(initialData = {}) {
    this.effectOptionsContainer.innerHTML = '';
    const type = this.controls.typeSelect.value;

    if (type === 'metallic') {
      this.createMetallicDirectionOptions(initialData);
    } else if (type === 'holographic') {
      this.createHolographicDirectionOptions(initialData);
    }
    // glass and neon have no extra options
  }

  /**
   * Create metallic direction options (H/V)
   */
  createMetallicDirectionOptions(initialData = {}) {
    const directionSelect = this.builder.createSelect([
      { value: '0deg', label: 'Horizontal' },
      { value: '90deg', label: 'Vertical' }
    ], initialData.metallicDirection || '0deg');
    directionSelect.addEventListener('change', () => {
      if (this.onChange) this.onChange();
    });
    const directionRow = this.builder.createRow('Shine Direction', directionSelect);
    this.effectOptionsContainer.appendChild(directionRow);
    this.controls.metallicDirection = directionSelect;
  }

  /**
   * Create holographic direction options (H/V)
   */
  createHolographicDirectionOptions(initialData = {}) {
    const directionSelect = this.builder.createSelect([
      { value: '0deg', label: 'Horizontal' },
      { value: '90deg', label: 'Vertical' }
    ], initialData.holographicDirection || '0deg');
    directionSelect.addEventListener('change', () => {
      if (this.onChange) this.onChange();
    });
    const directionRow = this.builder.createRow('Rainbow Direction', directionSelect);
    this.effectOptionsContainer.appendChild(directionRow);
    this.controls.holographicDirection = directionSelect;
  }

  getData() {
    const type = this.controls.typeSelect.value;
    const data = {
      type: 'special',
      effectType: type,
      color: this.controls.baseColor.value,
      opacity: parseFloat(this.controls.opacitySlider.slider.value)
    };

    // Add type-specific options
    if (type === 'metallic' && this.controls.metallicDirection) {
      data.metallicDirection = this.controls.metallicDirection.value;
    } else if (type === 'holographic' && this.controls.holographicDirection) {
      data.holographicDirection = this.controls.holographicDirection.value;
    }

    return data;
  }

  setData(data) {
    // Set effect type
    if (data.effectType) {
      this.controls.typeSelect.value = data.effectType;
    }

    // Update options container for the selected type
    this.updateEffectOptions(data);

    // Set base color
    if (data.color) {
      this.controls.baseColor.value = data.color;
    }

    // Set opacity
    if (data.opacity !== undefined) {
      this.controls.opacitySlider.slider.value = data.opacity;
      this.controls.opacitySlider.valueInput.value = data.opacity;
    }
  }
}
